<script lang="ts">
  import { app } from './appState.svelte';
  import NewNotebookDialog from './NewNotebookDialog.svelte';
  import SearchBar from './SearchBar.svelte';
  import SyncDot from './SyncDot.svelte';
  import { coverStyle } from './covers';
  import type { Notebook } from '../store/types';

  let notebooks = $state<Notebook[]>([]);
  let showNew = $state(false);
  let showSettings = $state(false);
  let singleFingerDraws = $state(false);
  let shapeSnap = $state(true);
  $effect(() => {
    void app.repo!.getSettings().then((s) => {
      singleFingerDraws = s.singleFingerDraws;
      shapeSnap = s.shapeSnap;
    });
  });
  async function toggleSingleFinger() {
    singleFingerDraws = !singleFingerDraws;
    await app.repo!.patchSettings({ singleFingerDraws });
  }
  // Engines read shapeSnap at create (InkEngine.create), so an already-open
  // editor picks a change up on its next page load — fine, since this toggle
  // lives on the library screen where no editor is open.
  async function toggleShapeSnap() {
    shapeSnap = !shapeSnap;
    await app.repo!.patchSettings({ shapeSnap });
  }

  // ---- sync settings ----
  let syncToken = $state('');
  let testResult = $state<{ ok: boolean; message: string } | null>(null);
  let syncBusy = $state(false);
  // Prefill the (masked) token field from syncmeta when the panel opens.
  $effect(() => {
    if (!showSettings) return;
    void app.repo!.getSyncMeta('syncToken').then((t) => { if (typeof t === 'string' && !syncToken) syncToken = t; });
  });

  async function toggleSync() {
    if (syncBusy) return;
    syncBusy = true;
    try {
      if (app.syncStatus === 'disabled') {
        if (!syncToken.trim()) { testResult = { ok: false, message: 'Enter a device token first.' }; return; }
        await app.enableSync(syncToken.trim());
      } else {
        await app.disableSync();
      }
    } finally { syncBusy = false; }
  }

  async function testConnection() {
    testResult = null;
    try {
      const res = await fetch('/api/health', { headers: { Authorization: `Bearer ${syncToken.trim()}` } });
      testResult = res.ok
        ? { ok: true, message: 'Connected.' }
        : { ok: false, message: res.status === 401 ? 'Token rejected.' : `Server error (${res.status}).` };
    } catch {
      testResult = { ok: false, message: 'Could not reach the server.' };
    }
  }

  async function refresh() { notebooks = (await app.repo!.listNotebooks()); }
  $effect(() => { void refresh(); });

  // Live refresh: a pull that applied any remote rows (incl. notebook-only
  // changes, which emit no pagechanged) re-fetches the notebook list.
  $effect(() => {
    const sync = app.syncEngine;
    if (!sync) return;
    const onApplied = () => void refresh();
    sync.on('applied', onApplied);
    return () => sync.off('applied', onApplied);
  });

  async function open(nb: Notebook) {
    // First LIVE page — pageOrder retains tombstoned ids (local or remote deletes).
    for (const pageId of nb.pageOrder) {
      const page = await app.repo!.getPage(pageId);
      if (page && !page.deletedAt) {
        app.route = { screen: 'editor', notebookId: nb.id, pageId };
        return;
      }
    }
  }

  // ---- Import PDF (M4 spec §2.1) ----
  // parse meta (lazy pdf.ts) → upload once → notebook with one fixed pdfRef
  // page per PDF page → open. createNotebook always seeds a first blank page;
  // the import PURGES that seed page after creating the pdfRef pages (purge,
  // not soft-delete, so no phantom blank page ever shows up in Trash — the
  // page is seconds old, empty, and never seen, so hard-delete is safe; the
  // server treats a purge of an unknown/just-created page as an idempotent
  // no-op and the outbox scrub even drops the seed page's create mutation).
  let importInput: HTMLInputElement | undefined;
  let importing = $state(false);
  const PDF_MAX_BYTES = 25 * 1024 * 1024;

  async function importPdf(ev: Event) {
    const input = ev.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = ''; // re-selecting the same file must fire change again
    if (!file || importing) return;
    if (file.size > PDF_MAX_BYTES) { app.showToast('PDF is too large (25MB max).'); return; }
    importing = true;
    try {
      const bytes = await file.arrayBuffer();
      // Lazy: pdfjs-dist must never enter the initial JS graph.
      const { parsePdfMeta } = await import('../ink/pdf');
      const meta = await parsePdfMeta(bytes);
      if (meta.pageCount === 0) throw new Error('PDF has no pages');
      // Sync disabled → local-only import: uploadFile skips the POST, mints a
      // local id and caches the bytes in IDB. Enabling sync later does NOT
      // retro-upload the file (v1) — warn at import time.
      const localOnly = app.syncStatus === 'disabled';
      const fileId = await app.uploadFile(bytes, file.name, file.type || 'application/pdf');
      const title = file.name.replace(/\.pdf$/i, '').trim() || 'Imported PDF';
      const { notebook, page: seedPage } = await app.repo!.createNotebook({
        title, cover: 0, defaultTemplate: 'blank', defaultKind: 'fixed',
      });
      let firstPageId = '';
      for (let i = 0; i < meta.pageCount; i++) {
        const p = await app.repo!.createPage(notebook.id, {
          kind: 'fixed', templateId: 'blank',
          pdfRef: { fileId, pageNo: i + 1 }, size: meta.sizes[i],
        });
        if (i === 0) firstPageId = p.id;
      }
      await app.repo!.purgePage(seedPage.id);
      app.showToast(localOnly
        ? 'Saved locally — enable sync before importing to share across devices.'
        : `Imported “${title}”`);
      app.route = { screen: 'editor', notebookId: notebook.id, pageId: firstPageId };
    } catch (e) {
      app.showToast(`Import failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      importing = false;
    }
  }

  async function remove(nb: Notebook, ev: Event) {
    ev.stopPropagation();
    await app.repo!.softDeleteNotebook(nb.id);
    await refresh();
    app.showToast(`Deleted “${nb.title}”`, async () => { await app.repo!.restoreNotebook(nb.id); await refresh(); });
  }
</script>

<div class="library">
  <header>
    <h1>Claude Cote</h1>
    <div class="actions">
      <SearchBar />
      <SyncDot status={app.syncStatus} />
      <button class="theme" onclick={() => app.setThemeOverride(app.theme === 'dark' ? 'light' : 'dark')} title="Toggle theme">
        {app.theme === 'dark' ? '☀️' : '🌙'}
      </button>
      <button onclick={() => (app.route = { screen: 'trash' })} title="Trash">🗑</button>
      <button onclick={() => (showSettings = !showSettings)} title="Settings">⚙</button>
      <button onclick={() => importInput?.click()} disabled={importing} title="Import PDF">
        {importing ? 'Importing…' : 'Import PDF'}
      </button>
      <button class="new" onclick={() => (showNew = true)}>+ Notebook</button>
    </div>
  </header>

  {#if showSettings}
    <div class="settings">
      <label>
        <input type="checkbox" checked={singleFingerDraws} onchange={toggleSingleFinger} />
        Single finger draws (instead of pans)
      </label>
      <label>
        <input type="checkbox" checked={shapeSnap} onchange={toggleShapeSnap} />
        Snap shapes when holding
      </label>
      <div class="sync">
        <div class="sync-head">Sync</div>
        <div class="sync-row">
          <input
            type="password"
            placeholder="Device token"
            autocomplete="off"
            bind:value={syncToken}
            disabled={app.syncStatus !== 'disabled'}
          />
          <button class="sync-btn" onclick={toggleSync} disabled={syncBusy}>
            {app.syncStatus === 'disabled' ? 'Enable' : 'Disable'}
          </button>
          <button class="sync-btn" onclick={testConnection} disabled={!syncToken.trim()}>Test connection</button>
        </div>
        {#if testResult}
          <div class="sync-line" class:bad={!testResult.ok}>{testResult.ok ? '✓' : '✗'} {testResult.message}</div>
        {/if}
        <div class="sync-line">Status: {app.syncStatus}</div>
      </div>
    </div>
  {/if}

  {#if notebooks.length === 0}
    <div class="empty"><p>No notebooks yet — create your first one.</p></div>
  {/if}

  <div class="grid">
    {#each notebooks as nb (nb.id)}
      <!-- div[role=button] (not <button>) so the delete control can be a real
           button without nesting interactive elements inside a native button -->
      <div
        class="card"
        role="button"
        tabindex="0"
        aria-label="Open “{nb.title}”"
        onclick={() => open(nb)}
        onkeydown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); void open(nb); } }}
      >
        <div class="cover" style="background:{coverStyle(nb.cover)}"></div>
        <div class="meta">
          <span class="title">{nb.title}</span>
          <button class="del" title="Delete" aria-label="Delete “{nb.title}”" onclick={(e) => remove(nb, e)}>🗑</button>
        </div>
      </div>
    {/each}
  </div>
</div>

<!-- Hidden picker for Import PDF (M4 spec §2.1). -->
<input type="file" accept="application/pdf,.pdf" hidden bind:this={importInput} onchange={importPdf} />

{#if showNew}
  <NewNotebookDialog onclose={() => (showNew = false)} oncreated={refresh} />
{/if}

<style>
  .library { padding: 20px; max-width: 1100px; margin: 0 auto; }
  header { display: flex; justify-content: space-between; align-items: center; }
  h1 { font-size: 22px; }
  .actions { display: flex; gap: 10px; align-items: center; }
  button { border: none; cursor: pointer; border-radius: var(--radius-panel); background: var(--surface); color: var(--ink-primary); padding: 10px 16px; min-height: 44px; }
  .new { background: var(--accent); color: #fff; }
  .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 18px; margin-top: 18px; }
  .card { padding: 0; overflow: hidden; box-shadow: var(--shadow-soft); text-align: left; background: var(--surface); border-radius: var(--radius-panel); cursor: pointer; }
  .cover { aspect-ratio: 3/4; }
  .meta { display: flex; justify-content: space-between; align-items: center; padding: 4px 4px 4px 12px; }
  .title { font-weight: 600; font-size: 13px; }
  .del { opacity: 0.5; padding: 0; min-width: 44px; min-height: 44px; display: grid; place-items: center; background: transparent; }
  .del:hover { opacity: 1; }
  .empty { text-align: center; opacity: 0.6; padding: 60px 0; }
  .settings { margin-top: 12px; padding: 12px 16px; background: var(--surface); border-radius: var(--radius-panel); font-size: 13px; }
  .settings label { display: flex; align-items: center; gap: 8px; }
  .settings label + label { margin-top: 8px; }
  .sync { margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--hairline); display: flex; flex-direction: column; gap: 8px; }
  .sync-head { font-weight: 600; }
  .sync-row { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
  .sync-row input[type='password'] { flex: 1; min-width: 180px; min-height: 44px; padding: 0 12px; border: 1px solid var(--hairline); border-radius: var(--radius-panel); background: var(--paper); color: var(--ink-primary); font: inherit; }
  .sync-btn { background: var(--paper); }
  .sync-btn:disabled { opacity: 0.4; cursor: default; }
  .sync-line { opacity: 0.75; }
  .sync-line.bad { color: #c0564a; opacity: 1; }
</style>
